import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import get from 'lodash.get';
import {
  Box,
  Card,
  Checkbox,
  FlexBox,
  Grid,
  MuiBox,
  Typography,
  color,
  useMediaQuery,
  useTheme
} from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { useActions } from 'hooks/useActions';
import { actions as cartActions, selectors as cartSelectors, Cart } from 'services/cart';
import { Configuration, selectors as configSelectors, LanguageField, Message } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { formatCurrency } from 'utils/stringFormat';
import { useStyles } from './styled';

const DonationView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string };

  const { applyDonation, resetDonationError, setDonationSource } = useActions({
    applyDonation: cartActions.applyDonation,
    resetDonationError: cartActions.resetDonationError,
    setDonationSource: cartActions.setDonationSource
  });

  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const reserving = useSelector(cartSelectors.selectReserving);
  const donation = useSelector(cartSelectors.selectDonation);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const languageSettings: LanguageField[] = get(configuration, 'configuration.languageSettings', []);
  const palette = get(configuration, 'configuration.globalSettings.palette');
  const ticketsUnavailable: Message = get(configuration, `messaging.${i18n.language}.ticketsUnavailable`, {});
  const support = get(configuration, `${ticketingPath}.supportName.${i18n.language}`) || i18n.t('commonText.support');
  const errorOverlayHeader = get(ticketsUnavailable, 'header', i18n.t('ticketingRoute.ticketsUnavailable'));
  const errorOverlaySubheader = get(
    ticketsUnavailable,
    'message',
    i18n.t('saleError.additionalAssistance', { support })
  );
  const errorOverlayConfirm = get(ticketsUnavailable, 'buttons[0]', i18n.t('ticketingRoute.lookOtherTickets'));
  const amounts: number[] = get(configuration, `${ticketingPath}.donations.amounts`, []);
  amounts.length = Math.min(amounts.length, 6);
  const messages = get(configuration, `${ticketingPath}.donations.messages`, {});
  const header = get(messages, `header.${i18n.language}`, t('donation.pageHeader'));
  const subheader = get(messages, `subheader.${i18n.language}`);
  const giftAidEnabled = get(configuration, `${ticketingPath}.donations.giftAid.enabled`, false);
  const giftAidMessages = get(configuration, `${ticketingPath}.donations.giftAid.messages`, {});
  const giftAidOptIn = get(giftAidMessages, `optIn.${i18n.language}`, t('donation.giftAidOptIn'));
  const giftAidDetails = get(giftAidMessages, `details.${i18n.language}`, t('donation.giftAidDetails'));

  const classes = useStyles()();
  const themes = useTheme();
  const matchPortrait = useMediaQuery(themes.breakpoints.down('lg'));

  const [selectedTile, setSelectedTile] = React.useState<number | null>(null);
  const [giftAidChecked, setGiftAidChecked] = React.useState<boolean>(false);
  const [showGiftAidOverlay, setShowGiftAidOverlay] = React.useState<boolean>(false);
  const [uiLoading, setUILoading] = React.useState<boolean>(false);

  // Nav forward on tile selection if gift aid is disabled
  React.useEffect(() => {
    if (typeof selectedTile === 'number' && !giftAidEnabled) onNextPageClick();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTile]);

  // Show the disclaimer for Gift Aid when it is checked
  React.useEffect(() => {
    if (giftAidChecked) setShowGiftAidOverlay(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftAidChecked]);

  // Navigate to the cart if the donation was applied successfully
  React.useEffect(() => {
    if (typeof selectedTile === 'number' && cart && !reserving && !donation.error) {
      setDonationSource('DonationView');
      navigate('/ticketing/cart', { state: { from: location.pathname } });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reserving]);

  // Set uiLoading to false if there was an error
  React.useEffect(() => {
    if (donation.error) setUILoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donation.error]);

  // Select/deselect the tile
  const onTileClick = (idx: number) => {
    if (selectedTile === idx) {
      setSelectedTile(null);
    } else {
      setSelectedTile(idx);
    }
  };

  // Navigate back
  const onPrevPageClick = () => {
    let pathname = '/ticketing/start-purchase';
    if (state && state.from === '/ticketing/events') {
      pathname = '/ticketing/events';
    }
    navigate(pathname, { state: { from: location.pathname } });
  };

  // Add the donation to the cart
  const onNextPageClick = () => {
    setUILoading(true);
    const donationAmount = amounts[selectedTile as number];
    applyDonation(parseFloat(donationAmount.toFixed(2)));
  };

  // Render the loading overlay
  if (reserving || uiLoading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  // Render the error overlay
  if (donation.error) {
    return (
      <ActionOverlay
        title={
          <Typography variant="h2" align="center">
            {errorOverlayHeader}
          </Typography>
        }
        confirmText={errorOverlayConfirm}
        height={660}
        description={
          <Box width={600}>
            <Typography variant="h4" align="center">
              {errorOverlaySubheader}
            </Typography>
          </Box>
        }
        onConfirm={resetDonationError}
      />
    );
  }

  return (
    <>
      <Grid container className={classes.outerContainer}>
        <Grid container className={classes.innerContainer}>
          {/* Title */}
          <MuiBox className={classes.headerContainer}>
            <Typography variant="h2">{header}</Typography>
            <Typography fontSize={30}>{subheader}</Typography>
          </MuiBox>

          {amounts.length > 0 ? (
            <>
              {/* Container of tiles for the amounts */}
              <Grid
                container
                className={classes.tilesContainer}
                sx={{
                  gridTemplateColumns: `repeat(${matchPortrait ? 2 : 3}, 1fr)`
                }}
              >
                {amounts.map((amount, idx) => {
                  const isSelected = typeof selectedTile === 'number' && selectedTile === idx;

                  return (
                    <>
                      <Card
                        fullHeight
                        hasBorder={isSelected}
                        borderColor={palette?.primary}
                        key={idx}
                        sx={{ boxShadow: 'none' }}
                      >
                        <Card.ActionArea onClick={() => onTileClick(idx)}>
                          <FlexBox
                            border={`solid 1px ${color.LIGHT_GRAY}`}
                            padding="2 4"
                            align="center"
                            justify="between"
                            sx={{ minHeight: '80px' }}
                          >
                            <Checkbox
                              size="medium"
                              label={
                                <Typography fontSize={32} fontWeight={600}>
                                  {formatCurrency(amount, {}, languageSettings)}
                                </Typography>
                              }
                              checked={isSelected ? true : false}
                              className={classes.tileCheckbox}
                            />
                          </FlexBox>
                        </Card.ActionArea>
                      </Card>
                    </>
                  );
                })}
              </Grid>

              {/* Gift Aid checkbox */}
              {giftAidEnabled && (
                <FlexBox align="center" justify="center" maxHeight="300px">
                  <Checkbox
                    size="large"
                    data-testid="gift-aid-checkbox"
                    label={
                      <Typography fontSize="32px" fontWeight={600} sx={{ lineHeight: 1 }}>
                        {giftAidOptIn}
                      </Typography>
                    }
                    checked={giftAidChecked}
                    onChange={() => setGiftAidChecked(!giftAidChecked)}
                  />
                </FlexBox>
              )}
            </>
          ) : (
            <>
              {/* No amounts are configured to show */}
              <FlexBox height="600px" align="center">
                <Typography fontSize={30}>{t('commonText.nothingToDisplay')}</Typography>
              </FlexBox>
            </>
          )}
        </Grid>
      </Grid>

      {/* Footer */}
      <Footer
        onPrevClick={onPrevPageClick}
        nextText={giftAidEnabled ? t('commonText.next') : undefined}
        onNextClick={onNextPageClick}
        isNextDisabled={typeof selectedTile !== 'number'}
      />

      {/* Gift Aid details overlay */}
      {showGiftAidOverlay && (
        <ActionOverlay
          title={''}
          description={
            <Typography fontSize={32} fontWeight={600} align="center">
              {giftAidDetails}
            </Typography>
          }
          confirmText={t('commonText.confirm')}
          onConfirm={() => setShowGiftAidOverlay(false)}
          style={{ zIndex: '2147483648' }}
        />
      )}
    </>
  );
};

export default DonationView;
